import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, ref } from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import ImageUploadDialog from '@/components/dialogs/ImageUploadDialog.vue';
import Account from '@/model/Account';
import Locale from '@/model/Locale';
import TimeZone from '@/model/TimeZone';
import Country from '@/model/Country';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import { performSaveAction } from '@/services/vue-utils';
import DeleteAccountDialog from '@/components/views/settings/DeleteAccountDialog.vue';
import httpClient from '@/api/http-client';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import ImagePreviewField from '@/components/forms/ImagePreviewField.vue';
import copy from 'copy-to-clipboard';
import useStoreWatch from '@/composables/store-watch';
import store from '@/store/store';
import { ValidationObserver } from 'vee-validate';
import i18n from '@/plugins/i18n';
import useUnsavedGuard from '@/composables/unsaved-guard';
import { useToast } from '../helpers/vue2-helpers';

interface TimezoneMenuItem {
  text?: string; value?: number; header?:string; divider?:boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Account',
  setup(__props) {

// Removed i18n:
// label.regional_settings

const toast = useToast();

const observerRef = ref(null as null | InstanceType<typeof ValidationObserver>);

const a = ref(new Account());
const olda = ref(new Account());

const uploadDialog = ref(false);
const deleteAccountDialog = ref(false);

const timezones = ref([] as TimeZone[]);
const locales = ref([] as Locale[]);
const countries = ref([] as Country[]);

const isChanged = computed(() => !isModelEqualToModel(a.value, olda.value));
useUnsavedGuard(isChanged);

const timezoneItems = computed(() => {
  const tzItems = [] as TimezoneMenuItem[];
  let prevKey = '';
  timezones.value.forEach((tz) => {
    const key = tz.name.split('/')[0].trim();
    if (key !== prevKey) {
      if (tzItems.length > 0) { tzItems.push({ divider: true }); }
      tzItems.push({ header: key });
      prevKey = key;
    }
    tzItems.push({ text: tz.name.replace(`${key} / `, '').trim(), value: tz.id });
  });
  return tzItems;
});

const autofillCountry = async () => {
  try {
    const countryCode = await httpClient.getCountryCode();
    if (!countryCode || a.value.countryCode) return; // no country code detected or no need to update it any more
    Vue.set(a.value, 'countryCode', countryCode); // autofill country code
  } catch (e) {
    console.log('Autofill country code error: ', e);
  }
};

const reset = async () => {
  const acctSettings = store.state.settings.account;
  a.value = cloneModel(acctSettings);
  olda.value = acctSettings;

  locales.value = store.getters.sortedLocales;
  countries.value = store.getters.sortedCountries;
  timezones.value = store.getters.sortedTimezones;

  if (!a.value.countryCode) autofillCountry();
};
useStoreWatch(reset);

const save = async () => {
  const ok = await performSaveAction(
    observerRef.value,
    async () => store.dispatch('sendAccount', { entity: a.value }),
  );
  if (ok) {
    reset();
  }
};

const updateLogoLink = async (image: File | null) => {
  const ok = await performSaveAction(
    undefined,
    async () => {
      let imageUrl = '';

      // save image and get url, if undefined, image is being removed
      if (image !== null) {
        const key = store.state.settings.account.serviceManagerAccessKey ?? '';
        imageUrl = await httpClient.uploadImageFile(image, key);
      }

      // save account
      olda.value.logoLink = imageUrl;
      a.value.logoLink = imageUrl;
      await store.dispatch('sendAccount', { entity: olda.value });
    },
  );

  if (ok) {
    uploadDialog.value = false;
  }
};

const copyAccountId = () => {
  copy(`${a.value.id}`);
  toast.success(i18n.t('message.account_id_copied'));
};

const defaultCC = computed(() => a.value.countryCode);

const tripadvisorDomains = [
  'https://www.tripadvisor.com/',
  'https://www.tripadvisor.co.uk/',
  'https://www.tripadvisor.ca/',
  'https://www.tripadvisor.com.au/',
  'https://www.tripadvisor.in/',
  'https://www.tripadvisor.de/',
  'https://www.tripadvisor.fr/',
  'https://www.tripadvisor.es/',
  'https://www.tripadvisor.it/',
  'https://www.tripadvisor.nl/',
  'https://www.tripadvisor.jp/',
  'https://www.tripadvisor.com.br/',
  'https://www.tripadvisor.com.mx/',
  'https://www.tripadvisor.se/',
  'https://www.tripadvisor.dk/',
  'https://www.tripadvisor.no/',
  'https://www.tripadvisor.fi/',
  'https://www.tripadvisor.ie/',
  'https://www.tripadvisor.sg/',
  'https://www.tripadvisor.co.nz/',
  'https://www.tripadvisor.co.za/',
  'https://www.tripadvisor.com.tr/',
  'https://www.tripadvisor.com.ar/',
  'https://www.tripadvisor.com.pe/',
  'https://www.tripadvisor.com.ve/',
  'https://www.tripadvisor.com.co/',
  'https://www.tripadvisor.com.ec/',
  'https://www.tripadvisor.com.ph/',
  'https://www.tripadvisor.com.my/',
  'https://www.tripadvisor.com.hk/',
  'https://www.tripadvisor.com.tw/',
  'https://www.tripadvisor.co.kr/',
  'https://www.tripadvisor.com.vn/',
  'https://www.tripadvisor.com.id/',
  'https://www.tripadvisor.com.th/',
  'https://www.tripadvisor.com.pk/',
  'https://www.tripadvisor.com.sa/',
  'https://www.tripadvisor.com.eg/',
  'https://www.tripadvisor.com.il/',
  'https://www.tripadvisor.com.ua/',
  'https://www.tripadvisor.pl/',
  'https://www.tripadvisor.cz/',
  'https://www.tripadvisor.hu/',
  'https://www.tripadvisor.ro/',
  'https://www.tripadvisor.bg/',
  'https://www.tripadvisor.hr/',
  'https://www.tripadvisor.si/',
  'https://www.tripadvisor.sk/',
  'https://www.tripadvisor.lt/',
  'https://www.tripadvisor.lv/',
  'https://www.tripadvisor.ee/',
  'https://www.tripadvisor.is/',
  'https://www.tripadvisor.pt/',
];


return { __sfc: true,toast, observerRef, a, olda, uploadDialog, deleteAccountDialog, timezones, locales, countries, isChanged, timezoneItems, autofillCountry, reset, save, updateLogoLink, copyAccountId, defaultCC, tripadvisorDomains, useSettingsBreadcrumbs, SettingsForm, ImageUploadDialog, DeleteAccountDialog, SettingsGroup, ImagePreviewField, ValidationObserver }
}

})